import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../ui/table';
import {
  Settings2, AlertCircle, AlertTriangle, Info, Copy, RotateCcw, Plus, Trash2, Target, Gauge,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend,
  ResponsiveContainer, ReferenceLine, ReferenceArea,
} from 'recharts';
import {
  DEFAULT_INPUTS, FIELD_SPECS, MAX_VEHICLE_SPEED_SPEC, GEAR_RATIO_FIELD_SPEC,
  ADVANCED_FIELD_SPECS, VEHICLE_MASS_SPEC, TARGET_FIELD_SPECS, DRIVETRAIN_TYPES,
  MIN_GEARS, MAX_GEARS, MAX_ALT_FINAL_DRIVES, DEFAULT_ALT_FINAL_DRIVES, PRESETS,
  KMPH_TO_MPH, validateField, validateOptionalField,
} from '../../lib/gearRatioTools';
import { gearRatioToolApi } from '../../lib/toolsApi';
import { useQueryPrefill } from '../../lib/queryPrefill';
import {
  SERIES_COLORS, CHART_GRID, CHART_AXIS, AXIS_TICK_STYLE, AXIS_LABEL_STYLE, LINE_PROPS,
  TOOLTIP_CONTENT_STYLE, TOOLTIP_LABEL_STYLE, LEGEND_STYLE,
} from './simulator/chartTheme';
import ChartZoomHeader from './simulator/ChartZoomHeader';
import { useChartZoom } from './simulator/useChartZoom';
import ReferenceLineTopLabel, { REFERENCE_LABEL_TOP_MARGIN, referenceLabelAnchorFor } from './simulator/ReferenceLineTopLabel';

const CALC_DEBOUNCE_MS = 350;
const SPEC_BY_KEY = Object.fromEntries(FIELD_SPECS.map((s) => [s.key, s]));

// Query-param prefill contract: ?tireWidthMm=225&tireAspectRatio=45&wheelDiameterIn=17
// (sent by the Tyre Size Calculator).
const PREFILL_SPECS = FIELD_SPECS.filter((s) => ['tireWidthMm', 'tireAspectRatio', 'wheelDiameterIn'].includes(s.key));

const fmt = (n, d = 2) => (n === null || n === undefined || Number.isNaN(n) ? '—' : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));
const round1 = (n) => Math.round(n * 10) / 10;
const toDisplaySpeed = (kmph, unit) => (unit === 'mph' ? kmph * KMPH_TO_MPH : kmph);
const speedUnitLabel = (unit) => (unit === 'mph' ? 'mph' : 'km/h');

function buildDefaultRaw() {
  const out = {};
  FIELD_SPECS.forEach((s) => { out[s.key] = String(DEFAULT_INPUTS[s.key]); });
  out.maxVehicleSpeedKmph = '';
  out.motorTorqueNm = String(DEFAULT_INPUTS.motorTorqueNm);
  out.driveEfficiencyPercent = String(DEFAULT_INPUTS.driveEfficiencyPercent);
  out.vehicleMassKg = '';
  out.targetSpeedKmph = '';
  out.targetRpm = '';
  return out;
}

const FieldLabel = ({ children, unit, tip, htmlFor }) => (
  <div className="flex items-center gap-1.5">
    <Label htmlFor={htmlFor} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
      {children} {unit && <span className="normal-case text-muted-foreground/70">({unit})</span>}
    </Label>
    {tip && (
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{tip}</TooltipContent>
      </Tooltip>
    )}
  </div>
);

const GearRatioCalculator = () => {
  const [drivetrain, setDrivetrain] = useState(DEFAULT_INPUTS.drivetrain);
  const [advancedMode, setAdvancedMode] = useState(DEFAULT_INPUTS.advancedMode);
  const [speedUnit, setSpeedUnit] = useState('kmph');

  const prefill = useQueryPrefill(PREFILL_SPECS, validateField);
  const [raw, setRaw] = useState(() => ({ ...buildDefaultRaw(), ...prefill.values }));
  const setField = (key, value) => setRaw((prev) => ({ ...prev, [key]: value }));

  const [gearRatios, setGearRatios] = useState(() => DEFAULT_INPUTS.gearRatios.map(String));
  const [altFinalDrives, setAltFinalDrives] = useState(() => DEFAULT_ALT_FINAL_DRIVES.map(String));
  const numGears = gearRatios.length;

  const setGearRatio = (i, value) => setGearRatios((prev) => prev.map((v, idx) => (idx === i ? value : v)));
  const addGear = () => {
    if (numGears >= MAX_GEARS) return;
    setGearRatios((prev) => {
      const last = Number(prev[prev.length - 1]);
      const next = Number.isFinite(last) && last > 0 ? (last * 0.83).toFixed(2) : '1.00';
      return [...prev, next];
    });
  };
  const removeGear = (i) => {
    if (numGears <= MIN_GEARS) return;
    setGearRatios((prev) => prev.filter((_, idx) => idx !== i));
  };

  const setAltFinalDrive = (i, value) => setAltFinalDrives((prev) => prev.map((v, idx) => (idx === i ? value : v)));
  const addAltFinalDrive = () => {
    if (altFinalDrives.length >= MAX_ALT_FINAL_DRIVES) return;
    setAltFinalDrives((prev) => [...prev, '4.00']);
  };
  const removeAltFinalDrive = (i) => setAltFinalDrives((prev) => prev.filter((_, idx) => idx !== i));

  const applyPreset = (key) => {
    const preset = PRESETS[key];
    if (!preset) return;
    setDrivetrain(preset.drivetrain);
    setField('tireWidthMm', String(preset.tireWidthMm));
    setField('tireAspectRatio', String(preset.tireAspectRatio));
    setField('wheelDiameterIn', String(preset.wheelDiameterIn));
    setField('finalDriveRatio', String(preset.finalDriveRatio));
    setField('maxRpm', String(preset.maxRpm));
    setField('referenceSpeedKmph', String(preset.referenceSpeedKmph));
    setGearRatios(preset.gearRatios.map(String));
  };

  const handleReset = () => {
    setDrivetrain(DEFAULT_INPUTS.drivetrain);
    setAdvancedMode(false);
    setSpeedUnit('kmph');
    setRaw(buildDefaultRaw());
    setGearRatios(DEFAULT_INPUTS.gearRatios.map(String));
    setAltFinalDrives(DEFAULT_ALT_FINAL_DRIVES.map(String));
  };

  const { fieldErrors, values, gearErrors, parsedGears, altErrors } = useMemo(() => {
    const errors = {};
    const parsed = {};
    FIELD_SPECS.forEach((spec) => {
      const err = validateField(spec, raw[spec.key]);
      if (err) errors[spec.key] = err;
      else parsed[spec.key] = Number(raw[spec.key]);
    });

    const maxSpeedErr = validateOptionalField(MAX_VEHICLE_SPEED_SPEC, raw.maxVehicleSpeedKmph);
    if (maxSpeedErr) errors.maxVehicleSpeedKmph = maxSpeedErr;

    if (advancedMode) {
      ADVANCED_FIELD_SPECS.forEach((spec) => {
        const err = validateField(spec, raw[spec.key]);
        if (err) errors[spec.key] = err;
        else parsed[spec.key] = Number(raw[spec.key]);
      });
    }
    const massErr = validateOptionalField(VEHICLE_MASS_SPEC, raw.vehicleMassKg);
    if (massErr) errors.vehicleMassKg = massErr;

    if (raw.targetSpeedKmph !== '' || raw.targetRpm !== '') {
      TARGET_FIELD_SPECS.forEach((spec) => {
        const err = validateField(spec, raw[spec.key]);
        if (err) errors[spec.key] = err;
      });
    }

    const gErrors = gearRatios.map((g) => validateField(GEAR_RATIO_FIELD_SPEC, g));
    const gParsed = gearRatios.map((g, i) => (gErrors[i] ? null : Number(g)));

    const aErrors = altFinalDrives.map((v) => validateField(SPEC_BY_KEY.finalDriveRatio, v));

    return { fieldErrors: errors, values: parsed, gearErrors: gErrors, parsedGears: gParsed, altErrors: aErrors };
  }, [raw, gearRatios, altFinalDrives, advancedMode]);

  const hasErrors = Object.keys(fieldErrors).length > 0 || gearErrors.some(Boolean);

  const [results, setResults] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (hasErrors) { setResults(null); setCalcError(null); return; }

    const validAltFDs = altFinalDrives
      .map((v, i) => (altErrors[i] ? null : Number(v)))
      .filter((n) => n !== null);

    const payload = {
      drivetrain,
      tireWidthMm: values.tireWidthMm,
      tireAspectRatio: values.tireAspectRatio,
      wheelDiameterIn: values.wheelDiameterIn,
      maxRpm: values.maxRpm,
      finalDriveRatio: values.finalDriveRatio,
      referenceSpeedKmph: values.referenceSpeedKmph,
      maxVehicleSpeedKmph: raw.maxVehicleSpeedKmph === '' ? null : Number(raw.maxVehicleSpeedKmph),
      numGears,
      gearRatios: parsedGears,
      advancedMode,
      motorTorqueNm: advancedMode ? values.motorTorqueNm : undefined,
      driveEfficiencyPercent: advancedMode ? values.driveEfficiencyPercent : undefined,
      vehicleMassKg: raw.vehicleMassKg === '' ? null : Number(raw.vehicleMassKg),
      targetSpeedKmph: raw.targetSpeedKmph === '' ? null : Number(raw.targetSpeedKmph),
      targetRpm: raw.targetRpm === '' ? null : Number(raw.targetRpm),
      altFinalDrives: validAltFDs.length ? validAltFDs : undefined,
    };

    const requestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      gearRatioToolApi.calculate(payload)
        .then((outcome) => {
          if (requestId !== requestIdRef.current) return;
          if (outcome.errors) {
            setResults(null);
            setCalcError(Object.values(outcome.errors)[0]);
          } else {
            setResults(outcome.results);
            setCalcError(null);
          }
        })
        .catch((e) => { if (requestId === requestIdRef.current) setCalcError(e.message); });
    }, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasErrors, values, gearRatios, altFinalDrives, drivetrain, advancedMode, raw.maxVehicleSpeedKmph, raw.vehicleMassKg, raw.targetSpeedKmph, raw.targetRpm]);

  const chartData = useMemo(() => {
    if (!results?.curves?.length) return [];
    const pointCount = results.curves[0].points.length;
    const rows = [];
    for (let i = 0; i < pointCount; i++) {
      const row = { rpm: results.curves[0].points[i].rpm };
      results.curves.forEach((c) => { row[`gear${c.gear}`] = round1(toDisplaySpeed(c.points[i].speedKmph, speedUnit)); });
      rows.push(row);
    }
    return rows;
  }, [results, speedUnit]);

  const handleCopy = async () => {
    if (!results) return;
    const unitLabel = speedUnitLabel(speedUnit);
    const lines = [
      `Gear Ratio & Final Drive Ratio Calculator — ${drivetrain}`,
      `Tire: ${raw.tireWidthMm}/${raw.tireAspectRatio} R${raw.wheelDiameterIn} — diameter ${fmt(results.tireGeometry.diameterM, 3)} m, circumference ${fmt(results.tireGeometry.circumferenceM, 3)} m`,
      '',
      `Gear | Gear Ratio | Final Drive | Overall Ratio | Speed @ Max RPM | RPM @ ${fmt(values.referenceSpeedKmph, 0)} km/h`,
      ...results.gearTable.map((r) => `${r.gear} | ${fmt(r.gearRatio, 3)} | ${fmt(r.finalDriveRatio, 3)} | ${fmt(r.overallRatio, 3)} | ${fmt(toDisplaySpeed(r.speedAtMaxRpmKmph, speedUnit), 1)} ${unitLabel} | ${fmt(r.rpmAtReferenceSpeed, 0)} rpm`),
    ];
    if (results.finalDriveComparison?.length) {
      lines.push('', 'Final Drive Comparison | Speed @ Max RPM | Cruising RPM | Wheel Torque Multiplier');
      results.finalDriveComparison.forEach((r) => {
        lines.push(`${fmt(r.finalDriveRatio, 2)} | ${fmt(toDisplaySpeed(r.speedAtMaxRpmKmph, speedUnit), 1)} ${unitLabel} | ${fmt(r.cruisingRpmAtReferenceSpeed, 0)} rpm | ${fmt(r.wheelTorqueMultiplier, 2)}×`);
      });
    }
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      toast.success('Results copied to clipboard.');
    } catch {
      toast.error('Could not copy to clipboard.');
    }
  };

  const numField = (key, { tip } = {}) => {
    const spec = SPEC_BY_KEY[key] || ADVANCED_FIELD_SPECS.find((s) => s.key === key);
    const error = fieldErrors[key];
    const id = `gear-ratio-${key}`;
    return (
      <div className="space-y-2" key={key}>
        <FieldLabel htmlFor={id} unit={spec.unit} tip={tip}>{spec.label}</FieldLabel>
        <Input
          id={id}
          type="number"
          step="any"
          value={raw[key]}
          onChange={(e) => setField(key, e.target.value)}
          className="font-mono"
          aria-invalid={!!error}
          data-testid={`${id}-input`}
        />
        {error && (
          <p className="flex items-center gap-1 text-xs text-destructive" data-testid={`${id}-error`}>
            <AlertCircle className="h-3 w-3 shrink-0" /> {error}
          </p>
        )}
      </div>
    );
  };

  const optionalNumField = (key, spec, { tip, placeholder } = {}) => {
    const error = fieldErrors[key];
    const id = `gear-ratio-${key}`;
    return (
      <div className="space-y-2" key={key}>
        <FieldLabel htmlFor={id} unit={spec.unit} tip={tip}>{spec.label} <span className="text-muted-foreground/60">(optional)</span></FieldLabel>
        <Input
          id={id}
          type="number"
          step="any"
          value={raw[key]}
          onChange={(e) => setField(key, e.target.value)}
          className="font-mono"
          placeholder={placeholder}
          aria-invalid={!!error}
          data-testid={`${id}-input`}
        />
        {error && (
          <p className="flex items-center gap-1 text-xs text-destructive" data-testid={`${id}-error`}>
            <AlertCircle className="h-3 w-3 shrink-0" /> {error}
          </p>
        )}
      </div>
    );
  };

  return (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-6">
      <Card className="technical-card">
        <CardHeader>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-autoxium-blue" />
                Gear Ratio &amp; Final Drive Ratio Calculator
              </CardTitle>
              <CardDescription>
                Model how transmission gear ratios and final drive ratio relate engine/motor
                RPM to wheel speed and vehicle speed, per gear. Results update live as you edit.
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="sm" onClick={() => applyPreset('ice')} data-testid="gear-ratio-preset-ice">ICE Preset</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => applyPreset('ev')} data-testid="gear-ratio-preset-ev">EV Preset</Button>
              <Button type="button" variant="outline" size="sm" onClick={handleCopy} disabled={!results} data-testid="gear-ratio-copy-btn">
                <Copy className="h-3.5 w-3.5" /> Copy
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={handleReset} data-testid="gear-ratio-reset-btn">
                <RotateCcw className="h-3.5 w-3.5" /> Reset
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      <div className="grid lg:grid-cols-5 gap-6">
        <Card className="technical-card lg:col-span-3">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-base">Vehicle, Drivetrain &amp; Gearing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="p-4 bg-muted/50 rounded-md space-y-4">
              <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">Vehicle / Drivetrain</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Drivetrain</Label>
                  <Select value={drivetrain} onValueChange={setDrivetrain}>
                    <SelectTrigger data-testid="gear-ratio-drivetrain-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DRIVETRAIN_TYPES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {numField('wheelDiameterIn')}
                {numField('tireWidthMm')}
                {numField('tireAspectRatio')}
              </div>
              {results && (
                <p className="text-xs text-muted-foreground font-mono pt-1 border-t border-structure">
                  Tire diameter <span className="text-foreground font-bold">{fmt(results.tireGeometry.diameterM, 3)} m</span>
                  {' · '}circumference <span className="text-foreground font-bold">{fmt(results.tireGeometry.circumferenceM, 3)} m</span>
                </p>
              )}
            </div>

            <div className="p-4 bg-muted/50 rounded-md space-y-4">
              <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">Powertrain</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {numField('maxRpm')}
                {optionalNumField('maxVehicleSpeedKmph', MAX_VEHICLE_SPEED_SPEC, { tip: 'Caps Speed @ Max RPM in every gear, e.g. an electronic speed limiter. Leave blank for no limiter.', placeholder: 'None' })}
                {numField('finalDriveRatio', { tip: 'Fixed reduction ratio applied in the differential/final drive, in every gear.' })}
                {numField('referenceSpeedKmph', { tip: 'The speed used for the "RPM @ Reference Speed" column and the cruising-RPM comparison below.' })}
              </div>
            </div>

            <div className="p-4 bg-muted/50 rounded-md space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">Gear Ratios ({numGears})</h3>
                <Button type="button" variant="outline" size="sm" onClick={addGear} disabled={numGears >= MAX_GEARS} data-testid="gear-ratio-add-gear-btn">
                  <Plus className="h-3.5 w-3.5" /> Add Gear
                </Button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {gearRatios.map((g, i) => (
                  <div className="space-y-1.5" key={i}>
                    <div className="flex items-center gap-1.5">
                      <FieldLabel htmlFor={`gear-ratio-row-${i}`} unit=":1" tip={i === 0 ? 'The reduction ratio through the transmission for this gear — wheel-side shaft turns × Gear Ratio = engine/motor shaft turns.' : undefined}>
                        Gear {i + 1}
                      </FieldLabel>
                      {numGears > MIN_GEARS && (
                        <button type="button" onClick={() => removeGear(i)} className="ml-auto text-muted-foreground hover:text-destructive" data-testid={`gear-ratio-remove-gear-${i}-btn`} aria-label={`Remove gear ${i + 1}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    <Input
                      id={`gear-ratio-row-${i}`}
                      type="number"
                      step="any"
                      value={g}
                      onChange={(e) => setGearRatio(i, e.target.value)}
                      className="font-mono"
                      aria-invalid={!!gearErrors[i]}
                      data-testid={`gear-ratio-row-${i}-input`}
                    />
                    {gearErrors[i] && (
                      <p className="flex items-center gap-1 text-xs text-destructive"><AlertCircle className="h-3 w-3 shrink-0" /> {gearErrors[i]}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-muted/50 rounded-md space-y-4">
              <div className="flex items-center gap-3">
                <Switch id="gear-ratio-advanced" checked={advancedMode} onCheckedChange={setAdvancedMode} data-testid="gear-ratio-advanced-switch" />
                <Label htmlFor="gear-ratio-advanced" className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Automotive Engineering Mode (wheel torque / tractive force)
                </Label>
                <Tooltip>
                  <TooltipTrigger asChild><Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" /></TooltipTrigger>
                  <TooltipContent className="max-w-xs text-xs">Adds motor/engine torque, drivetrain efficiency, and an optional vehicle mass, and computes wheel torque, tractive force, and an estimated (theoretical) acceleration per gear.</TooltipContent>
                </Tooltip>
              </div>
              {advancedMode && (
                <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-structure">
                  {numField('motorTorqueNm')}
                  {numField('driveEfficiencyPercent', { tip: 'Fraction of motor/engine torque delivered to the wheels after transmission, driveshaft, and final-drive losses.' })}
                  {optionalNumField('vehicleMassKg', VEHICLE_MASS_SPEC, { tip: 'Enables an estimated theoretical acceleration alongside wheel torque and tractive force. Ignores aerodynamic drag, rolling resistance, and load transfer.', placeholder: 'None' })}
                </div>
              )}
            </div>

            <div className="p-4 bg-muted/50 rounded-md space-y-4">
              <div className="flex items-center gap-1.5">
                <Target className="h-3.5 w-3.5 text-autoxium-blue" />
                <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">Solve for Required Ratio</h3>
              </div>
              <p className="text-xs text-muted-foreground">Enter a target vehicle speed and RPM to see the overall (and, at the current Final Drive Ratio, gear) ratio that would achieve it.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                {optionalNumField('targetSpeedKmph', TARGET_FIELD_SPECS[0], { placeholder: 'e.g. 200' })}
                {optionalNumField('targetRpm', TARGET_FIELD_SPECS[1], { placeholder: 'e.g. 6500' })}
              </div>
              {results?.requiredRatio && (
                <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-structure text-sm font-mono">
                  <div className="flex justify-between"><span className="text-muted-foreground">Required Overall Ratio</span><span className="font-bold text-autoxium-blue">{fmt(results.requiredRatio.requiredOverallRatio, 3)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Required Gear Ratio</span><span className="font-bold text-autoxium-blue">{fmt(results.requiredRatio.requiredGearRatio, 3)}</span></div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="lg:col-span-2 space-y-4">
          {calcError ? (
            <Card className="technical-card"><CardContent className="pt-6">
              <div className="flex items-center gap-2 text-sm text-destructive" data-testid="gear-ratio-calc-error"><AlertCircle className="h-4 w-4 shrink-0" /> {calcError}</div>
            </CardContent></Card>
          ) : hasErrors ? (
            <Card className="technical-card"><CardContent className="pt-6">
              <p className="text-sm text-muted-foreground" data-testid="gear-ratio-results-placeholder">Fix the highlighted fields to see results.</p>
            </CardContent></Card>
          ) : !results ? (
            <Card className="technical-card"><CardContent className="pt-6">
              <p className="text-sm text-muted-foreground" data-testid="gear-ratio-results-loading">Calculating…</p>
            </CardContent></Card>
          ) : (
            <KpiPanel results={results} numGears={numGears} speedUnit={speedUnit} setSpeedUnit={setSpeedUnit} />
          )}
        </div>
      </div>

      {results && !hasErrors && (
        <>
          <GearTableSection results={results} speedUnit={speedUnit} referenceSpeedKmph={values.referenceSpeedKmph} />
          <ChartSection chartData={chartData} results={results} speedUnit={speedUnit} maxRpm={values.maxRpm} />
          <FinalDriveComparisonSection results={results} altFinalDrives={altFinalDrives} altErrors={altErrors}
            setAltFinalDrive={setAltFinalDrive} addAltFinalDrive={addAltFinalDrive} removeAltFinalDrive={removeAltFinalDrive}
            speedUnit={speedUnit} finalDriveRatio={values.finalDriveRatio} advancedMode={advancedMode} />
          {advancedMode && <AdvancedResultsSection results={results} />}
        </>
      )}
    </div>
    </TooltipProvider>
  );
};

// ---------------------------------------------------------------------------
// KPI panel
// ---------------------------------------------------------------------------

const Kpi = ({ label, value, unit, testId, accent }) => (
  <div className="p-3 bg-muted/50 rounded-md" data-testid={testId}>
    <p className="text-muted-foreground uppercase tracking-wider text-[10px] font-mono mb-1">{label}</p>
    <p className={`font-mono text-lg font-black ${accent ? 'text-autoxium-blue' : 'text-foreground'}`}>{value} <span className="text-xs font-normal text-muted-foreground">{unit}</span></p>
  </div>
);

const KpiPanel = ({ results, numGears, speedUnit, setSpeedUnit }) => {
  const topSpeedRow = results.gearTable.find((r) => r.gear === results.highlights.topSpeedGear);
  const unitLabel = speedUnitLabel(speedUnit);
  return (
    <Card className="technical-card ring-1 ring-autoxium-blue/30" data-testid="gear-ratio-results">
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="font-chivo uppercase tracking-tight text-base">Summary</CardTitle>
          <Select value={speedUnit} onValueChange={setSpeedUnit}>
            <SelectTrigger className="w-24 h-8" data-testid="gear-ratio-speed-unit-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="kmph">km/h</SelectItem><SelectItem value="mph">mph</SelectItem></SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Kpi label="Gears" value={numGears} unit="" testId="gear-ratio-kpi-gears" />
          <Kpi label="Drivetrain" value={results.drivetrain} unit="" testId="gear-ratio-kpi-drivetrain" />
          <Kpi label="1st Gear Overall Ratio" value={fmt(results.overallReductionRatio, 3)} unit=":1" testId="gear-ratio-kpi-overall" accent />
          <Kpi label={`Top Speed (Gear ${results.highlights.topSpeedGear ?? '—'})`} value={fmt(toDisplaySpeed(topSpeedRow?.speedAtMaxRpmKmph, speedUnit), 1)} unit={unitLabel} testId="gear-ratio-kpi-topspeed" accent />
        </div>
        {results.gearTable.some((r) => r.speedLimited) && (
          <div className="flex items-start gap-1.5 text-xs text-amber-800 bg-amber-500/10 border border-amber-500/30 rounded-md p-2.5">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> One or more gears are capped by the Maximum Vehicle Speed limiter rather than by Max RPM (marked *).
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Gear table
// ---------------------------------------------------------------------------

const GearTableSection = ({ results, speedUnit, referenceSpeedKmph }) => {
  const unitLabel = speedUnitLabel(speedUnit);
  return (
    <Card className="technical-card">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-sm">Per-Gear Results</CardTitle>
        <CardDescription>1st gear, the highest gear, and the top-speed gear (which may differ from the highest gear if a speed limiter is applied) are highlighted.</CardDescription>
      </CardHeader>
      <CardContent>
        <Table className="text-xs font-mono min-w-[820px]" data-testid="gear-ratio-table">
          <TableHeader>
            <TableRow className="text-muted-foreground uppercase tracking-wider text-[10px] hover:bg-transparent">
              <TableHead className="text-left h-auto py-2 pr-3">Gear</TableHead>
              <TableHead className="text-right h-auto py-2 pr-3">Gear Ratio</TableHead>
              <TableHead className="text-right h-auto py-2 pr-3">Final Drive</TableHead>
              <TableHead className="text-right h-auto py-2 pr-3">Overall Ratio</TableHead>
              <TableHead className="text-right h-auto py-2 pr-3">Speed @ Max RPM</TableHead>
              <TableHead className="text-right h-auto py-2 pr-3">RPM @ {fmt(referenceSpeedKmph, 0)} km/h</TableHead>
              <TableHead className="text-right h-auto py-2 pr-3">RPM Drop to Next</TableHead>
              <TableHead className="text-right h-auto py-2">Speed Range</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {results.gearTable.map((row) => {
              const isFirst = row.gear === results.highlights.firstGear;
              const isHighest = row.gear === results.highlights.highestGear;
              const isTopSpeed = row.gear === results.highlights.topSpeedGear;
              return (
                <TableRow key={row.gear} className={isTopSpeed ? 'bg-autoxium-blue/5' : ''} data-testid={`gear-ratio-table-row-${row.gear}`}>
                  <TableCell className="py-2 pr-3 font-bold whitespace-nowrap">
                    {row.gear}
                    {isFirst && <Badge variant="outline" className="ml-1.5 text-[9px] align-middle">1st</Badge>}
                    {isHighest && <Badge variant="outline" className="ml-1.5 text-[9px] align-middle">Highest</Badge>}
                    {isTopSpeed && <Badge className="ml-1.5 text-[9px] align-middle bg-autoxium-blue">Top Speed</Badge>}
                  </TableCell>
                  <TableCell className="text-right pr-3">{fmt(row.gearRatio, 3)}</TableCell>
                  <TableCell className="text-right pr-3">{fmt(row.finalDriveRatio, 3)}</TableCell>
                  <TableCell className="text-right pr-3 font-bold">{fmt(row.overallRatio, 3)}</TableCell>
                  <TableCell className="text-right pr-3">{fmt(toDisplaySpeed(row.speedAtMaxRpmKmph, speedUnit), 1)} {unitLabel}{row.speedLimited ? ' *' : ''}</TableCell>
                  <TableCell className="text-right pr-3">{fmt(row.rpmAtReferenceSpeed, 0)}</TableCell>
                  <TableCell className="text-right pr-3">{row.rpmDropPercent !== null ? `${fmt(row.rpmDropPercent, 1)}%` : '—'}</TableCell>
                  <TableCell className="text-right">{fmt(toDisplaySpeed(row.speedRangeKmph[0], speedUnit), 0)}–{fmt(toDisplaySpeed(row.speedRangeKmph[1], speedUnit), 0)} {unitLabel}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Chart
// ---------------------------------------------------------------------------

const ChartSection = ({ chartData, results, speedUnit, maxRpm }) => {
  const unitLabel = speedUnitLabel(speedUnit);
  const zoom = useChartZoom([0, 'dataMax']);
  return (
    <Card className="technical-card">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-sm">Gear vs. Vehicle Speed</CardTitle>
        <CardDescription>Each line is one gear's speed-vs-RPM relationship; the dashed reference line marks Maximum RPM.</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartZoomHeader isZoomed={zoom.isZoomed} onReset={zoom.resetZoom} testId="zoom-reset-gear-ratio" />
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData} margin={{ top: REFERENCE_LABEL_TOP_MARGIN, right: 16, left: 8, bottom: 24 }}
              onMouseDown={zoom.onMouseDown} onMouseMove={zoom.onMouseMove} onMouseUp={zoom.onMouseUp}
            >
              <CartesianGrid stroke={CHART_GRID} vertical={false} />
              <XAxis dataKey="rpm" type="number" domain={zoom.xDomain} allowDataOverflow tick={AXIS_TICK_STYLE}
                label={{ value: 'Engine/Motor RPM', position: 'insideBottom', offset: -12, style: AXIS_LABEL_STYLE }} />
              <YAxis tick={AXIS_TICK_STYLE} width={60}
                label={{ value: `Vehicle Speed (${unitLabel})`, angle: -90, position: 'insideLeft', style: AXIS_LABEL_STYLE }} />
              <RechartsTooltip
                contentStyle={TOOLTIP_CONTENT_STYLE}
                labelStyle={TOOLTIP_LABEL_STYLE}
                formatter={(v, name) => [`${fmt(v, 1)} ${unitLabel}`, name]}
                labelFormatter={(v) => `${fmt(v, 0)} RPM`}
              />
              <Legend wrapperStyle={{ ...LEGEND_STYLE, paddingTop: 18 }} />
              <ReferenceLine x={maxRpm} stroke="#dc2626" strokeDasharray="4 4" label={<ReferenceLineTopLabel text="Max RPM" color="#dc2626" anchor={referenceLabelAnchorFor(maxRpm, zoom.xDomain, chartData, 'rpm')} />} />
              {results.gearTable.map((row, i) => (
                <Line key={row.gear} type="monotone" dataKey={`gear${row.gear}`} name={`Gear ${row.gear}`} stroke={SERIES_COLORS[i % SERIES_COLORS.length]} {...LINE_PROPS} />
              ))}
              {zoom.isSelecting && (
                <ReferenceArea x1={zoom.refAreaLeft} x2={zoom.refAreaRight} stroke={CHART_AXIS} fill={CHART_AXIS} fillOpacity={0.15} />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Final Drive Comparison
// ---------------------------------------------------------------------------

const FinalDriveComparisonSection = ({ results, altFinalDrives, altErrors, setAltFinalDrive, addAltFinalDrive, removeAltFinalDrive, speedUnit, finalDriveRatio, advancedMode }) => {
  const unitLabel = speedUnitLabel(speedUnit);
  const comparison = results.finalDriveComparison || [];
  const hasTorqueDetail = comparison.some((r) => r.wheelTorqueNmLaunchGear !== undefined);
  return (
    <Card className="technical-card">
      <CardHeader>
        <CardTitle className="font-chivo uppercase tracking-tight text-sm">Final Drive Comparison</CardTitle>
        <CardDescription>
          Current Final Drive Ratio ({fmt(finalDriveRatio, 2)}) is the 1.00× baseline for Wheel Torque Multiplier.
          A higher numerical final-drive ratio increases wheel torque and acceleration potential in the launch gear,
          but increases cruising RPM and reduces vehicle speed at a given engine/motor RPM in the top gear.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {altFinalDrives.map((v, i) => (
            <div className="space-y-1" key={i}>
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  step="any"
                  value={v}
                  onChange={(e) => setAltFinalDrive(i, e.target.value)}
                  className="font-mono w-24 h-8"
                  aria-invalid={!!altErrors[i]}
                  data-testid={`gear-ratio-alt-fd-${i}-input`}
                />
                <button type="button" onClick={() => removeAltFinalDrive(i)} className="text-muted-foreground hover:text-destructive" data-testid={`gear-ratio-alt-fd-${i}-remove-btn`} aria-label={`Remove alternative final drive ${i + 1}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
              {altErrors[i] && <p className="text-[10px] text-destructive">{altErrors[i]}</p>}
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addAltFinalDrive} disabled={altFinalDrives.length >= MAX_ALT_FINAL_DRIVES} data-testid="gear-ratio-add-alt-fd-btn">
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        </div>

        <Table className="text-xs font-mono min-w-[720px]" data-testid="gear-ratio-fd-comparison-table">
          <TableHeader>
            <TableRow className="text-muted-foreground uppercase tracking-wider text-[10px] hover:bg-transparent">
              <TableHead className="text-left h-auto py-2 pr-3">Final Drive</TableHead>
              <TableHead className="text-right h-auto py-2 pr-3">Speed @ Max RPM (Top Gear)</TableHead>
              <TableHead className="text-right h-auto py-2 pr-3">Cruising RPM</TableHead>
              <TableHead className="text-right h-auto py-2 pr-3">Overall Gearing (Top Gear)</TableHead>
              <TableHead className="text-right h-auto py-2 pr-3">Wheel Torque Multiplier</TableHead>
              {hasTorqueDetail && <TableHead className="text-right h-auto py-2">Est. Launch Accel</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {comparison.map((row) => (
              <TableRow key={row.finalDriveRatio}>
                <TableCell className="py-2 pr-3 font-bold">{fmt(row.finalDriveRatio, 2)}</TableCell>
                <TableCell className="text-right pr-3">{fmt(toDisplaySpeed(row.speedAtMaxRpmKmph, speedUnit), 1)} {unitLabel}</TableCell>
                <TableCell className="text-right pr-3">{fmt(row.cruisingRpmAtReferenceSpeed, 0)} rpm</TableCell>
                <TableCell className="text-right pr-3">{fmt(row.overallGearingTopGear, 3)}</TableCell>
                <TableCell className="text-right pr-3 font-bold text-autoxium-blue">{fmt(row.wheelTorqueMultiplier, 3)}×</TableCell>
                {hasTorqueDetail && (
                  <TableCell className="text-right">{row.estimatedAccelMs2LaunchGear !== undefined ? `${fmt(row.estimatedAccelMs2LaunchGear, 2)} m/s²` : '—'}</TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!advancedMode && (
          <p className="text-xs text-muted-foreground">Enable Automotive Engineering Mode above (with a vehicle mass) to see estimated acceleration alongside each final-drive option.</p>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Advanced Mode results
// ---------------------------------------------------------------------------

const AdvancedResultsSection = ({ results }) => (
  <Card className="technical-card">
    <CardHeader>
      <CardTitle className="font-chivo uppercase tracking-tight text-sm flex items-center gap-2">
        <Gauge className="h-4 w-4 text-autoxium-blue" /> Wheel Torque &amp; Tractive Force per Gear
      </CardTitle>
      <CardDescription>Wheel Torque = Motor Torque × Gear Ratio × Final Drive Ratio × Drivetrain Efficiency. Tractive Force = Wheel Torque ÷ Wheel Radius. Acceleration is an estimated theoretical value — aerodynamic drag, rolling resistance, and load transfer are not modeled.</CardDescription>
    </CardHeader>
    <CardContent>
      <Table className="text-xs font-mono min-w-[480px]" data-testid="gear-ratio-advanced-table">
        <TableHeader>
          <TableRow className="text-muted-foreground uppercase tracking-wider text-[10px] hover:bg-transparent">
            <TableHead className="text-left h-auto py-2 pr-3">Gear</TableHead>
            <TableHead className="text-right h-auto py-2 pr-3">Wheel Torque</TableHead>
            <TableHead className="text-right h-auto py-2 pr-3">Tractive Force</TableHead>
            <TableHead className="text-right h-auto py-2">Estimated Acceleration</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {(results.advancedTable || []).map((row) => (
            <TableRow key={row.gear}>
              <TableCell className="py-2 pr-3 font-bold">{row.gear}</TableCell>
              <TableCell className="text-right pr-3">{fmt(row.wheelTorqueNm, 1)} Nm</TableCell>
              <TableCell className="text-right pr-3">{fmt(row.tractiveForceN, 0)} N</TableCell>
              <TableCell className="text-right">{row.estimatedAccelMs2 !== null ? `${fmt(row.estimatedAccelMs2, 2)} m/s²` : '— (enter vehicle mass)'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

export default GearRatioCalculator;
